import React, { useState } from 'react';
import { lotteryAPI } from '../services/api';

function BuySharesModal({ draw, clubId, onClose, onSuccess, isPublic = false }) {
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const sharesRemaining = (draw.maxShares || draw.totalShares || 10) - (draw.sharesSold || 0);
  const pricePerShare = draw.sharePrice || 500; // cents
  const totalCost = quantity * pricePerShare;

  const handleConfirm = async () => {
    setLoading(true);
    setError('');
    try {
      const drawIdentifier = draw.localDrawId || draw.drawId || draw.id;
      if (isPublic) {
        await lotteryAPI.buyPublicShares(drawIdentifier, quantity);
      } else {
        await lotteryAPI.buyShares(clubId, drawIdentifier, quantity, {
          gameCode: draw.gameCode,
          gameName: draw.gameName,
          drawDate: draw.drawDate,
          drawNumber: draw.drawNumber,
        });
      }
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to buy shares. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}>Buy Shares</h3>
          <button style={styles.closeBtn} onClick={onClose}>&times;</button>
        </div>

        <div style={styles.body}>
          <div style={styles.drawInfo}>
            <div style={styles.gameName}>{draw.gameName || draw.gameCode || 'Lottery Draw'}</div>
            <div style={styles.drawDate}>
              Draw: {draw.drawDate ? new Date(draw.drawDate).toLocaleDateString('en-AU', {
                weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
              }) : 'TBD'}
            </div>
          </div>

          <div style={styles.details}>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Price per share</span>
              <span style={styles.detailValue}>${(pricePerShare / 100).toFixed(2)}</span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Shares remaining</span>
              <span style={styles.detailValue}>{sharesRemaining}</span>
            </div>
          </div>

          <div style={styles.quantitySection}>
            <label style={styles.quantityLabel}>Quantity</label>
            <div style={styles.quantityControls}>
              <button
                style={styles.quantityBtn}
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                disabled={quantity <= 1}
              >
                -
              </button>
              <input
                type="number"
                style={styles.quantityInput}
                value={quantity}
                onChange={(e) => {
                  const val = parseInt(e.target.value) || 1;
                  setQuantity(Math.min(Math.max(1, val), sharesRemaining));
                }}
                min={1}
                max={sharesRemaining}
              />
              <button
                style={styles.quantityBtn}
                onClick={() => setQuantity(Math.min(sharesRemaining, quantity + 1))}
                disabled={quantity >= sharesRemaining}
              >
                +
              </button>
            </div>
          </div>

          <div style={styles.totalSection}>
            <span style={styles.totalLabel}>Total Cost</span>
            <span style={styles.totalValue}>${(totalCost / 100).toFixed(2)}</span>
          </div>

          {error && <div style={styles.error}>{error}</div>}
        </div>

        <div style={styles.footer}>
          <button style={styles.cancelBtn} onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button style={styles.confirmBtn} onClick={handleConfirm} disabled={loading}>
            {loading ? 'Processing...' : `Buy ${quantity} Share${quantity > 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#1a1d23',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '420px',
    border: '1px solid #2a2d35',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid #2a2d35',
  },
  title: {
    margin: 0,
    color: '#fff',
    fontSize: '18px',
    fontWeight: '600',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: '#888',
    fontSize: '24px',
    cursor: 'pointer',
    padding: '0 4px',
  },
  body: {
    padding: '20px',
  },
  drawInfo: {
    marginBottom: '16px',
    paddingBottom: '16px',
    borderBottom: '1px solid #2a2d35',
  },
  gameName: {
    color: '#f0c040',
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '4px',
  },
  drawDate: {
    color: '#aaa',
    fontSize: '14px',
  },
  details: {
    marginBottom: '16px',
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
  },
  detailLabel: {
    color: '#999',
    fontSize: '14px',
  },
  detailValue: {
    color: '#fff',
    fontSize: '14px',
    fontWeight: '500',
  },
  quantitySection: {
    marginBottom: '16px',
  },
  quantityLabel: {
    color: '#999',
    fontSize: '14px',
    display: 'block',
    marginBottom: '8px',
  },
  quantityControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  quantityBtn: {
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    border: '1px solid #3a3d45',
    backgroundColor: '#2a2d35',
    color: '#fff',
    fontSize: '18px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  quantityInput: {
    width: '60px',
    height: '36px',
    borderRadius: '8px',
    border: '1px solid #3a3d45',
    backgroundColor: '#2a2d35',
    color: '#fff',
    textAlign: 'center',
    fontSize: '16px',
  },
  totalSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    backgroundColor: '#22252d',
    borderRadius: '8px',
    marginBottom: '12px',
  },
  totalLabel: {
    color: '#ccc',
    fontSize: '16px',
    fontWeight: '500',
  },
  totalValue: {
    color: '#4ade80',
    fontSize: '22px',
    fontWeight: '700',
  },
  error: {
    color: '#ef4444',
    fontSize: '14px',
    padding: '8px 12px',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    borderRadius: '6px',
    marginTop: '8px',
  },
  footer: {
    display: 'flex',
    gap: '12px',
    padding: '16px 20px',
    borderTop: '1px solid #2a2d35',
  },
  cancelBtn: {
    flex: 1,
    padding: '10px',
    borderRadius: '8px',
    border: '1px solid #3a3d45',
    backgroundColor: 'transparent',
    color: '#ccc',
    fontSize: '14px',
    cursor: 'pointer',
  },
  confirmBtn: {
    flex: 2,
    padding: '10px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#f0c040',
    color: '#1a1d23',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};

export default BuySharesModal;
